import { Button, ModuleWrapper, StickyActionBar } from '@/components'
import { Module } from '@/modules/companies/consts'
import type {
  SalaryModuleConfigNode,
  SalaryType,
} from '@/modules/companies/types.graphql'
import type { PromptResolve } from '@ur-solutions/react-components'
import type { UseFormReturn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ReactNode } from 'react'
import { useEffect, useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { PeriodAddonForm } from './PeriodAddonPrompt'
import { SalarySettingsAddons } from './salary-settings-addons'
import { SalarySettingsGeneral } from './salary-settings-general'
import { SalarySettingsOvertime } from './salary-settings-overtime'
import { SalarySettingsPause } from './salary-settings-pause'
import { SalarySettingsSalaryCodes } from './salary-settings-salary-codes'
import type { SalaryWorkTimeTab } from './salary-work-time-tabs'
import {
  SalaryWorkTimeTabs,
  useSalaryWorkTimeTab,
} from './salary-work-time-tabs'

export interface OvertimeLimitForm {
  threshold: number
  salaryCode: string | null
}

export interface SalarySettingsForm {
  salaryGroups: string[]
  userId: string
  dailyWorkLimit: number
  periodWorkLimit: number

  pauseDuration: Date
  pauseThreshold: Date
  countPaidPauseInTotalWork: boolean
  countAvailabilityInTotalWork: boolean
  countAvailabilityInOvertime: boolean
  calculateAddonsOnPaidPause: boolean
  paidPauseThreshold: Date
  subtractPaidPauseAfterTotalWorkThreshold: boolean

  salaryDateStart: number
  salaryDateEnd: number
  name: string
  useSalaryDayPayment: boolean
  groupSalaryOnVehicle: boolean

  salaryType: keyof typeof SalaryType

  defaultSalaryCode: string | null
  defaultSalaryCodeWhenOvertime: string | null
  absenceHourTransactionOvertimeSalaryCode: string | null
  salaryDayCode: string | null

  drivingSalaryCode: string | null
  otherWorkSalaryCode: string | null
  availabilitySalaryCode: string | null
  paidPauseSalaryCode: string | null
  dailyRestSalaryCode: string | null

  dayOvertimeSalaryCode: string | null
  overtimeCalendarItemSalaryCode: string | null
  unplannedWorkOvertimeSalaryCode: string | null

  overtimeLimits: OvertimeLimitForm[]

  useDayOvertime: boolean
  useWeekOvertime: boolean
  useMonthOvertime: boolean
  usePlannerForOvertime: boolean
  usePlannerForUndertime: boolean
  usePlanPeriodsForUndertime: boolean
  undertimeLimit: number
  undertimeSalaryCodes: string[]

  calculateWeekOvertimeInline: boolean
  calculateOvertimeAsAddon: boolean
  weekOvertimeSalaryCode: string | null
  monthOvertimeSalaryCode: string | null
  periodAddons: PeriodAddonForm[]
  periodOvertimes: PeriodAddonForm[]

  defaultDepartment: string | null
  availableDepartments: string[]

  useTimeAddons: boolean
  timeAddonSalaryCodes: string[]
  useTimeOverrides: boolean
  timeOverrideSalaryCodes: string[]
  useFixedAddons: boolean
  fixedAddonSalaryCodes: string[]

  holidayAllowances: string[]
}

export interface SalarySettingsTabProps {
  form: UseFormReturn<SalarySettingsForm>
  config: SalaryModuleConfigNode
}

interface SalarySettingsFormProps extends SalarySettingsTabProps {
  title: ReactNode
  headerActions?: ReactNode
  /** Addons saved on their own, shown in the addons tab below the form fields. */
  extraAddons?: ReactNode
  /** Show Save before anything is changed, e.g. when creating. */
  alwaysShowSave?: boolean

  onSubmit: PromptResolve<SalarySettingsForm | null>
}

const TABS: SalaryWorkTimeTab[] = [
  'general',
  'salaryCodes',
  'overtime',
  'pause',
  'addons',
]

export const SalarySettingsFormView: React.FC<SalarySettingsFormProps> = ({
  form,
  config,
  title,
  headerActions,
  extraAddons,
  alwaysShowSave = false,
  onSubmit,
}) => {
  const translations = useTranslate({
    save: 'common.save',
    saveCurrentChanges: 'common.save-current-changes',
  })
  const { formValues, formErrors, updateForm, formValid, formEdited } = form
  const showSave = alwaysShowSave || formEdited

  const overtimeLimitsValid = useMemo(() => {
    // Tiers only apply in day-only mode with inline overtime calculation
    // enabled against the static daily limit. In any other configuration tiers
    // are cleared on submit, so stale invalid entries must not block saving.
    if (!formValues.useDayOvertime) return true
    if (formValues.useWeekOvertime) return true
    if (formValues.usePlannerForOvertime) return true
    if (!formValues.calculateWeekOvertimeInline) return true
    const seen = new Set<number>()
    for (const limit of formValues.overtimeLimits) {
      if (limit.threshold <= formValues.dailyWorkLimit) return false
      if (seen.has(limit.threshold)) return false
      seen.add(limit.threshold)
      if (!limit.salaryCode) return false
    }
    return true
  }, [
    formValues.useDayOvertime,
    formValues.useWeekOvertime,
    formValues.usePlannerForOvertime,
    formValues.calculateWeekOvertimeInline,
    formValues.overtimeLimits,
    formValues.dailyWorkLimit,
  ])

  const hasPeriodAddons =
    config.name === Module.TIMESHEETS || config.name === Module.ACTIVITIES

  useEffect(() => {
    if (!hasPeriodAddons && formValues.periodAddons.length > 0)
      updateForm({ periodAddons: [] })
  }, [hasPeriodAddons, formValues.periodAddons.length, updateForm])

  const [tab, setTab] = useSalaryWorkTimeTab(TABS)
  const invalidTabs = {
    general: !!formErrors.salaryDateStart,
    overtime: !overtimeLimitsValid,
  }

  return (
    <ModuleWrapper title={title} headerActions={headerActions}>
      <div
        className={cn(
          'flex max-w-5xl flex-col gap-4',
          showSave && 'pb-20 md:pb-0'
        )}
      >
        <SalaryWorkTimeTabs
          tabs={TABS}
          active={tab}
          invalid={invalidTabs}
          onChange={setTab}
        />
        {tab === 'general' && (
          <SalarySettingsGeneral form={form} config={config} />
        )}
        {tab === 'salaryCodes' && (
          <SalarySettingsSalaryCodes form={form} config={config} />
        )}
        {tab === 'overtime' && (
          <SalarySettingsOvertime form={form} config={config} />
        )}
        {tab === 'pause' && <SalarySettingsPause form={form} config={config} />}
        {tab === 'addons' && (
          <>
            <SalarySettingsAddons form={form} config={config} />
            {extraAddons}
          </>
        )}
      </div>

      {showSave && (
        <StickyActionBar
          className="justify-end md:max-w-5xl"
          message={translations.saveCurrentChanges}
        >
          <Button
            type="button"
            size="lg"
            disabled={!formValid || !overtimeLimitsValid}
            onClick={() => onSubmit(formValues)}
          >
            {translations.save}
          </Button>
        </StickyActionBar>
      )}
    </ModuleWrapper>
  )
}
